// Etichette dei preventivi salvati, per amministratore: spec 2026-10-28 § 9, approvate da Alfonso parola per parola.
// Le altre (Nuovo preventivo, Differenza al mese, Elimina, Annulla, Riprova…) restano in scritte.ts.
import type { Invio, VoceElenco } from "@/api/preventivo";
import { dataBreve } from "@/lib/linkPreventivo";

type UltimoInvio = NonNullable<VoceElenco["ultimo_invio"]>;

const ROMA = "Europe/Rome";

/** «28/10», «2/11»: il giorno in Italia, senza lo zero davanti (come gli esempi del § 9). */
function giornoMese(iso: string): string {
  const p = new Intl.DateTimeFormat("it-IT", { day: "numeric", month: "numeric", timeZone: ROMA }).formatToParts(new Date(iso));
  const v = (t: string) => Number(p.find((x) => x.type === t)?.value);
  return `${v("day")}/${v("month")}`;
}

/** «15:42». */
function ora(iso: string): string {
  return new Intl.DateTimeFormat("it-IT", { hour: "2-digit", minute: "2-digit", hourCycle: "h23", timeZone: ROMA })
    .format(new Date(iso));
}

// Lista
export const TITOLO_LISTA = "Preventivi condomini";
export const CERCA_AMMINISTRATORE = "Cerca amministratore";
export const NON_INDICATO = "Amministratore non indicato";
export const NESSUN_PREVENTIVO = "Nessun preventivo salvato";
export const SENZA_NOME_IN_LISTA = "I preventivi senza nome compaiono nella lista come «Amministratore non indicato».";
export const NESSUNA_OFFERTA_SCELTA = "nessuna offerta scelta";
export const COL_AMMINISTRATORE = "Amministratore";
export const COL_CONDOMINI = "Condomìni";
export const COL_MODIFICATO = "Modificato";
export const COL_ULTIMO_INVIO = "Ultimo invio";

export function nessunAmministratore(q: string): string {
  return `Nessun amministratore con «${q}».`;
}

/** «7 condomìni · 16 forniture». */
export function rigaConteggio(condomini: number, forniture: number): string {
  const c = condomini === 1 ? "1 condominio" : `${condomini} condomìni`;
  const f = forniture === 1 ? "1 fornitura" : `${forniture} forniture`;
  return `${c} · ${f}`;
}

/** La cella «Condomìni» della tabella: «7 · 16 forniture». */
export function cellaConteggio(condomini: number, forniture: number): string {
  return `${condomini} · ${forniture === 1 ? "1 fornitura" : `${forniture} forniture`}`;
}

/** «Modificato il 28/10 · mail del 21/10» / «· WhatsApp del 21/10» / «· ancora non inviato». */
export function rigaDate(modificato: string, invio: UltimoInvio | null): string {
  const m = `Modificato il ${giornoMese(modificato)}`;
  if (!invio) return `${m} · ancora non inviato`;
  return `${m} · ${invio.canale === "mail" ? "mail" : "WhatsApp"} del ${giornoMese(invio.il)}`;
}

/** La cella «Ultimo invio» della tabella: «mail, 21/10/2026» / «WhatsApp, 21/10/2026» / «—». */
export function cellaInvio(invio: UltimoInvio | null): string {
  if (!invio) return "—";
  return `${invio.canale === "mail" ? "mail" : "WhatsApp"}, ${dataBreve(invio.il)}`;
}

// Preventivo
export const TORNA_LISTA = "Preventivi";
export const AMMINISTRATORE = "Amministratore";
export const NOME = "Nome";
export const EMAIL = "Email";
export const CELLULARE = "Cellulare";
export const FACOLTATIVO = "(facoltativo)";
export const EMAIL_NON_VALIDA = "Email non valida";
export const CELLULARE_NON_VALIDO = "Cellulare non valido";
export const SALVA = "Salva";
export const SALVATAGGIO = "Salvataggio…";
export const SALVATO = "Salvato";
export const PRIMA_SALVA = "Prima salva i dati dell'amministratore.";
export const USCIRE_SENZA_SALVARE = "Uscire senza salvare?";
export const TESTO_USCITA = "Le modifiche ai dati dell'amministratore non sono salvate.";
export const ESCI_SENZA_SALVARE = "Esci senza salvare";
export const MANCA_EMAIL = "Manca l'email dell'amministratore";
export const MAIL_NON_PARTITA = "Mail non partita";
export const INVII = "Invii";
export const ELIMINA_PREVENTIVO = "Elimina preventivo";

// L'invio all'amministratore in un pannello (mockup invio-finale, approvato da Alfonso il 29/10)
export const INVIA = "Invia";
export const INVIA_AMMINISTRATORE = "Invia all'amministratore";
export const TOGLI_TUTTI = "Togli tutti";
export const METTI_TUTTI = "Metti tutti";
export const MAIL = "Mail";
export const WHATSAPP = "WhatsApp";

/** «Condomìni nel link · 10 su 10». */
export function condominiNelLink(scelti: number, tutti: number): string {
  return `Condomìni nel link · ${scelti} su ${tutti}`;
}

/** Sotto «Invia all'amministratore» dal computer (variante D3, 30/10), con i pezzi già approvati della lista:
 *  «Ancora non inviato», «Ultimo invio: mail del 29/10», «Ultimo invio: WhatsApp del 21/10». */
export function statoInvio(invii: Invio[]): string {
  if (invii.length === 0) return "Ancora non inviato";
  const u = invii.reduce((a, b) => (Date.parse(b.il) > Date.parse(a.il) ? b : a));
  return `Ultimo invio: ${u.canale === "mail" ? "mail" : "WhatsApp"} del ${giornoMese(u.il)}`;
}

/** Sotto i tasti: «La mail va a amministrazione@studioferraro.it». */
export function laMailVaA(email: string): string {
  return `La mail va a ${email}`;
}

/** «Mail inviata il 28/10 alle 15:42 a {email}. Link valido fino al 27/11/2026.», «Link aperto su WhatsApp il 21/10,
 *  valido fino al 20/11/2026.»; scaduto: «… scaduto il 20/11.» */
export function rigaInvio(invio: Invio): string {
  const fino = invio.scaduto ? `scaduto il ${giornoMese(invio.scade_il)}` : `valido fino al ${dataBreve(invio.scade_il)}`;
  if (invio.canale === "whatsapp") return `Link aperto su WhatsApp il ${giornoMese(invio.il)}, ${fino}.`;
  const a = invio.destinatario ? ` a ${invio.destinatario}` : "";
  return `Mail inviata il ${giornoMese(invio.il)} alle ${ora(invio.il)}${a}. Link ${fino}.`;
}

export function titoloElimina(nome: string | null): string {
  return nome ? `Eliminare il preventivo di ${nome}?` : "Eliminare questo preventivo?";
}

export function testoElimina(n: number): string {
  const coda = "link mandati smettono di funzionare. Non si torna indietro.";
  // senza bollette si tolgono parole alla frase approvata, non se ne aggiungono
  if (n === 0) return `I ${coda}`;
  return `Si cancell${n === 1 ? "a la bolletta" : `ano le ${n} bollette`} e i ${coda}`;
}

/** Accanto all'offerta scelta che non è più in vendita: «scaduta il 2/11»; `data` è «2026-11-02». */
export function scadutaIl(data: string): string {
  const [, m, g] = data.split("-").map(Number);
  return `scaduta il ${g}/${m}`;
}

// Inoltro da WhatsApp
export const ULTIMO_PREVENTIVO = "Ultimo preventivo";
export const AGGIUNGI_ULTIMO = "Aggiungi all'ultimo preventivo";
export const AGGIUNGI_ALTRO = "Aggiungi a un altro preventivo";
export const IN_QUALE = "In quale preventivo?";
export const INDIETRO = "Indietro";

/** Il messaggio di WhatsApp; `fino` già scritta («27/11/2026»). */
export function testoWhatsApp(n: number, fino: string, link: string): string {
  const c = n === 1 ? "1 condominio" : `${n} condomìni`;
  return `Buongiorno, le mando l'analisi delle bollette della luce e il preventivo per ${c}. Si apre da qui fino al ${fino}: ${link}`;
}
